import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { HelpTooltip } from '@/components/ui/help-tooltip';
import type { Chart } from '../engine/chart';
import { signOf } from '../engine/aspects';
import { PLANETS, PLANET_MEANINGS, HOUSE_MEANINGS } from '../data/constants';
import { getPlanetInSign, getPlanetInHouse } from '../lib/contentLookup';

interface Props { chart: Chart }

const ROW_ORDER = ['Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn','Uranus','Neptune','Pluto','Chiron','Node'];

/**
 * Advanced-mode placement table. Each row is a collapsible accordion that
 * reveals the planet-in-sign and planet-in-house prose from the Phase 4
 * libraries. Sun, Moon, and Rising deliberately remain compact rows — the
 * Big Three card already carries their full prose.
 */
export const PlanetTable: React.FC<Props> = ({ chart }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const unknownTime = !!chart.unknownTime;

  const toggle = (k: string) => setOpen((s) => ({ ...s, [k]: !s[k] }));

  return (
    <Card className="p-5 bg-card/50">
      {unknownTime && (
        <p className="text-xs text-muted-foreground mb-3">
          Birth time unknown — sign and degree are accurate; house placements need an exact time.
        </p>
      )}
      <ul className="divide-y divide-border/40">
        {ROW_ORDER.map((name) => {
          const p = chart.placements[name];
          if (!p) return null;
          const def = PLANETS.find((pl) => pl.name === name);
          const s = signOf(p.longitude);
          const meaning = PLANET_MEANINGS[name];
          const houseLabel = p.house ? HOUSE_MEANINGS[p.house]?.title : null;
          const isBigThree = name === 'Sun' || name === 'Moon';
          const signEntry = isBigThree ? null : getPlanetInSign(name, s.sign);
          const houseEntry = isBigThree || !p.house ? null : getPlanetInHouse(name, p.house);
          const hasDetail = !!signEntry || !!houseEntry;
          const isOpen = !!open[name];

          return (
            <li key={name}>
              <Collapsible open={isOpen} onOpenChange={() => hasDetail && toggle(name)}>
                <CollapsibleTrigger
                  asChild
                  disabled={!hasDetail}
                  className={hasDetail ? 'cursor-pointer' : 'cursor-default'}
                >
                  <div className="flex items-center gap-3 py-2.5 hover:bg-muted/30 px-2 -mx-2 rounded-sm transition-colors">
                    <span
                      className="font-serif text-lg w-6 text-center shrink-0"
                      style={{ color: def?.color }}
                    >
                      {def?.glyph ?? name[0]}
                    </span>
                    <span className="w-20 shrink-0 font-medium text-sm">{name}</span>
                    <span className="font-serif text-base">{s.glyph}</span>
                    <span className="text-sm tabular-nums">{s.degree.toFixed(1)}° {s.sign}</span>
                    {!unknownTime && p.house && (
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        · House {p.house}{houseLabel ? ` (${houseLabel})` : ''}
                      </span>
                    )}
                    {unknownTime && (
                      <span className="hidden sm:inline-flex items-center gap-1 text-xs text-muted-foreground/70">
                        · House —
                        <HelpTooltip
                          side="top"
                          content="House placements need an exact birth time to calculate. Sign and degree are still accurate — they don't depend on time of day."
                          className="w-3 h-3"
                        />
                      </span>
                    )}
                    {p.retrograde && <span className="text-xs text-muted-foreground">℞</span>}
                    <span className="ml-auto flex items-center gap-2">
                      {hasDetail && (
                        <ChevronDown
                          className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                      )}
                    </span>
                  </div>
                </CollapsibleTrigger>
                {hasDetail && (
                  <CollapsibleContent className="pb-4 pt-1 px-2 space-y-3">
                    {meaning && (
                      <p className="text-xs text-muted-foreground italic">
                        {meaning.title} — {meaning.short}.
                      </p>
                    )}
                    {signEntry && (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-foreground/90">{signEntry.headline}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {signEntry.keyTraits.map((t) => (
                            <span
                              key={t}
                              className="text-[11px] px-2 py-0.5 rounded-full bg-muted/60 text-foreground/80"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                        <p className="text-sm text-foreground/85 leading-relaxed">{signEntry.paragraph}</p>
                        <p className="text-xs text-muted-foreground">
                          <span className="uppercase tracking-wider">Shadow:</span> {signEntry.shadow}
                        </p>
                      </div>
                    )}
                    {houseEntry && (
                      <div className="space-y-2 border-t border-border/30 pt-3">
                        <p className="text-sm font-medium text-foreground/90">{houseEntry.headline}</p>
                        <p className="text-sm text-foreground/85 leading-relaxed">{houseEntry.paragraph}</p>
                        <p className="text-xs text-muted-foreground">
                          <span className="uppercase tracking-wider">Shows up as:</span> {houseEntry.manifestation}
                        </p>
                      </div>
                    )}
                  </CollapsibleContent>
                )}
              </Collapsible>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};
